import { FormHelperText, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { alpha, styled } from '@mui/material/styles';

import SbButton, { SbLoadingButton } from '@Components/Button';
import DragDrop from '@Components/File/DragDrop';
import Modal from '@Components/Modal';

import Image from '@Components/Image';
import { FileResultType } from '../../../Helper/file';
import { readXslAndGetBulkAWBUploadObject } from './helper';
import { BulkAWBType } from './types';
import { downloadSampleAWBSheet } from './downloadSampleAWBSheet';
import API from '@Services/apiAxios';

interface PropsType {
  open: boolean;
  handleClose: () => void;
}

const bulkAWBUploadAPI = (payload: { shipments: any[] }) => {
  return API.post('/shipment/bulk-awb-upload', payload);
};

function FileError({ fileError }: { fileError: string }) {
  if (!fileError.trim()) return <></>;

  return (
    <FormHelperText error className="mt-1">
      {fileError}
    </FormHelperText>
  );
}

function BulkAWBUpload(props: PropsType) {
  const [xlsFileData, setXlsFileData] = useState<BulkAWBType[] | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  console.info({ loading, apiError });

  const onDone = async () => {
    if (!xlsFileData) return;

    try {
      setLoading(true);
      setApiError('');

      const payload = {
        shipments: xlsFileData,
      };

      await bulkAWBUploadAPI(payload);

      onClose(); // success close modal
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Bulk AWB upload failed';

      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (data: FileResultType[]) => {
    try {
      setFileError('');
      setFileName('');

      if (data?.[0]?.fileName) {
        setFileName(data[0].fileName);
      }

      const bulkAWBData = readXslAndGetBulkAWBUploadObject(data);
      setXlsFileData(bulkAWBData);
    } catch (error: any) {
      if (error?.msg) {
        setFileError(error.msg);
        setXlsFileData(null);
      }
    }
  };

  const onDownloadSampleSheet = () => {
    downloadSampleAWBSheet();
  };

  const onClose = () => {
    props.handleClose();
    setFileError('');
    setFileName('');
    setXlsFileData(null);
  };

  return (
    <Modal open={props.open} sx={{ minHeight: '470px', bottom: 0 }} maxWidth="sm">
      <Grid container>
        <Grid container item mb={1}>
          <Typography fontWeight="bold" variant="h6">
            Bulk AWB Upload
          </Typography>
        </Grid>

        <Grid container item>
          <DragDrop
            readType="xls"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            subText={fileName ? fileName : 'xlsx or xls file size no more than 10MB'}
            onChange={onFileChange}
          />

          <FileError fileError={fileError} />
        </Grid>

        <Grid container item mt={2} py={5} justifyContent="center">
          <Grid item xs={12} md="auto">
            <DownloadSampleSheet
              fullWidth
              variant="outlined"
              onClick={onDownloadSampleSheet}
              startIcon={
                <Image
                  src="/images/upload_document.svg"
                  alt="Speedbox"
                  width={35}
                  height={35}
                  style={{ transform: 'rotate(180deg)' }}
                />
              }
            >
              Download sample file
            </DownloadSampleSheet>

            <Typography variant="subtitle2" className="sb-text-gray text-center">
              xlsx or xls file size no more than 10MB
            </Typography>
          </Grid>
        </Grid>

        <Grid container item spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <SbButton variant="outlined" fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <SbLoadingButton variant="contained" fullWidth disabled={!xlsFileData} onClick={onDone} loading={false}>
              Done
            </SbLoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default BulkAWBUpload;

const DownloadSampleSheet = styled(SbButton)(({ theme }) => ({
  border: `1px solid #EAECF0`,
  borderRadius: '8px !important',
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
  '&:hover': {
    border: `1px solid #EAECF0`,
  },
}));
